import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  Plus,
  Minus,
  Loader2,
  Crown,
  Lock,
  DollarSign,
  Mail,
  X,
  Send,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * TeamSettings — manage seats + (Phase 2) invite team members.
 * Phase 1 lets the owner add/remove EXTRA seats above their plan's included seats.
 * Each extra seat costs SEAT_ADDON_USD / month (currently $5).
 */
export default function TeamSettings() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSending, setInviteSending] = useState(false);

  const loadInvites = async () => {
    try {
      const { data } = await http.get("/team/invites");
      setInvites(data.invites || []);
    } catch (err) {
      // Best-effort feature; surface in console for debugging without a toast
      // (this runs on every page load and a toast would be noisy).
      console.warn("Failed to load team invites:", err);
    }
  };

  useEffect(() => {
    let cancelled = false;
    http
      .get("/team")
      .then(({ data }) => {
        if (!cancelled) setTeam(data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Could not load team info");
      })
      .finally(() => !cancelled && setLoading(false));
    loadInvites();
    return () => {
      cancelled = true;
    };
  }, []);

  const sendInvite = async (e) => {
    e?.preventDefault?.();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setInviteSending(true);
    try {
      await http.post("/team/invites", { email });
      toast.success(`Invite sent to ${email}`);
      setInviteEmail("");
      await loadInvites();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not send invite");
    } finally {
      setInviteSending(false);
    }
  };

  const cancelInvite = async (id) => {
    try {
      await http.delete(`/team/invites/${id}`);
      toast.success("Invite cancelled");
      await loadInvites();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not cancel invite");
    }
  };

  const updateSeats = async (next) => {
    if (next < 0 || next > (team?.max_extra_seats ?? 50)) return;
    setBusy(true);
    try {
      const { data } = await http.put("/team/seats", { extra_seats: next });
      setTeam((prev) => ({ ...prev, ...data }));
      await refresh?.();
      const delta = next - (team?.extra_seats ?? 0);
      if (delta > 0) {
        toast.success(`+${delta} seat${delta === 1 ? "" : "s"} · $${delta * (team?.seat_addon_usd ?? 5)}/mo added`);
      } else if (delta < 0) {
        toast.success(`${delta} seat${delta === -1 ? "" : "s"} removed`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update seats");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !team) {
    return (
      <div className="grid place-items-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-ink-400" />
      </div>
    );
  }

  const tier = team.tier;
  const isFree = tier === "free";
  const isEnterprise = team.included_seats == null;
  const extra = team.extra_seats || 0;
  const included = team.included_seats || 0;
  const total = team.total_seats;
  const cost = team.monthly_addon_cost_usd || 0;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="team-page">
      <Link
        to="/dashboard"
        className="text-[10px] uppercase tracking-widestx font-semibold text-ink-400 hover:text-ink-900 inline-flex items-center gap-1 mb-6"
      >
        <ArrowLeft className="w-3 h-3" />
        Back to dashboard
      </Link>

      <div className="mb-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widestx font-mono text-ink-400">
        <Users className="w-3 h-3" />
        Team & Seats
      </div>
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-ink-900">
        Add teammates,
        <br />
        share one credit pool.
      </h1>
      <p className="text-sm text-ink-500 mt-3 max-w-2xl">
        Extra seats let your teammates use LeadIQ without splitting your monthly
        enrichment limit. Each extra user costs{" "}
        <span className="font-mono text-ink-900">${team.seat_addon_usd}/month</span>.
      </p>

      {/* Free / Enterprise edge cases */}
      {isFree && (
        <div
          data-testid="team-free-locked"
          className="mt-8 bg-white border border-ink-100 rounded-sm p-8 text-center"
        >
          <div className="inline-grid place-items-center w-12 h-12 rounded-sm bg-ink-100 mb-3">
            <Lock className="w-5 h-5 text-ink-700" />
          </div>
          <h2 className="font-display font-extrabold text-2xl tracking-tight">
            Team seats unlock on paid plans.
          </h2>
          <p className="text-sm text-ink-500 mt-2 max-w-md mx-auto">
            Upgrade to Starter or higher to invite teammates and share your
            enrichment credit pool.
          </p>
          <Button
            data-testid="team-free-upgrade"
            onClick={() => nav("/pricing")}
            className="mt-5 rounded-sm bg-ink-900 hover:bg-ink-700 text-white"
          >
            See plans
          </Button>
        </div>
      )}

      {isEnterprise && !isFree && (
        <>
          <div
            data-testid="team-enterprise"
            className="mt-8 bg-white border border-ink-100 rounded-sm p-8 text-center"
          >
            <div className="inline-grid place-items-center w-12 h-12 rounded-sm bg-[#E8F5E9] mb-3">
              <Crown className="w-5 h-5 text-[#2E7D32]" />
            </div>
            <h2 className="font-display font-extrabold text-2xl tracking-tight">
              Unlimited seats on Enterprise.
            </h2>
            <p className="text-sm text-ink-500 mt-2">
              Invite as many teammates as you need. No add-on fees.
            </p>
          </div>

          {/* Invite form + members list for Enterprise */}
          <div
            data-testid="team-members"
            className="mt-6 bg-white border border-ink-100 rounded-sm overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                Team members · {(team.members?.length || 0) + invites.filter((i) => i.status === "pending").length} active
              </div>
            </div>

            <form
              onSubmit={sendInvite}
              className="px-6 py-4 border-b border-ink-100 bg-ink-50/50 flex items-center gap-3"
              data-testid="invite-form"
            >
              <Mail className="w-4 h-4 text-ink-400 shrink-0" />
              <Input
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@company.com"
                className="flex-1 h-10 rounded-sm border-ink-200 focus:border-brand focus-visible:ring-brand font-mono text-sm"
                data-testid="invite-email-input"
              />
              <Button
                type="submit"
                disabled={inviteSending || !inviteEmail}
                className="h-10 px-4 rounded-sm bg-ink-900 hover:bg-ink-800 text-white font-medium tracking-wide"
                data-testid="invite-send-button"
              >
                {inviteSending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    Send invite
                  </>
                )}
              </Button>
            </form>

            <div>
              {(team.members || []).map((m) => (
                <div
                  key={m.id}
                  data-testid={`team-member-${m.id}`}
                  className="px-6 py-4 flex items-center justify-between border-b border-ink-100 last:border-b-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="grid place-items-center w-9 h-9 rounded-sm bg-ink-100 text-ink-700 font-mono font-bold text-sm">
                      {(m.name || m.email).slice(0, 1).toUpperCase()}
                    </div>
                    <div className="leading-tight">
                      <div className="text-sm font-semibold text-ink-900">{m.name}</div>
                      <div className="text-[11px] font-mono text-ink-500">{m.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.is_owner && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm bg-[#FFF8E1] text-[#F57F17]">
                        <Crown className="w-2.5 h-2.5" />
                        Owner
                      </span>
                    )}
                    <span className="text-[9px] uppercase tracking-widestx font-mono text-ink-400">
                      {m.role}
                    </span>
                  </div>
                </div>
              ))}

              {invites
                .filter((i) => i.status === "pending")
                .map((inv) => (
                  <div
                    key={inv.id}
                    data-testid={`invite-row-${inv.id}`}
                    className="px-6 py-4 flex items-center justify-between border-b border-ink-100 last:border-b-0"
                  >
                    <div className="flex items-center gap-3">
                      <div className="grid place-items-center w-9 h-9 rounded-sm bg-ink-100 text-ink-400">
                        <Clock className="w-4 h-4" />
                      </div>
                      <div className="leading-tight">
                        <div className="text-sm font-semibold text-ink-700">{inv.email}</div>
                        <div className="text-[11px] font-mono text-ink-400">Pending acceptance</div>
                      </div>
                    </div>
                    <button
                      onClick={() => cancelInvite(inv.id)}
                      className="text-[10px] uppercase tracking-widestx font-semibold text-ink-400 hover:text-red-600 inline-flex items-center gap-1"
                      data-testid={`cancel-invite-${inv.id}`}
                    >
                      <X className="w-3 h-3" />
                      Cancel
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}

      {/* Paid tier seat manager */}
      {!isFree && !isEnterprise && (
        <>
          {/* Summary card */}
          <div
            data-testid="team-summary"
            className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3"
          >
            <Stat label="Plan includes" value={`${included}`} suffix={`seat${included === 1 ? "" : "s"}`} />
            <Stat
              label="Extra seats"
              value={`${extra}`}
              suffix={`@ $${team.seat_addon_usd}/mo each`}
              accent={extra > 0}
            />
            <Stat
              label="Total team capacity"
              value={`${total}`}
              suffix={`seat${total === 1 ? "" : "s"}`}
              highlight
            />
          </div>

          {/* Seat purchase UI */}
          <div
            data-testid="team-seat-manager"
            className="mt-6 bg-white border border-ink-100 rounded-sm p-6 sm:p-8"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                  Buy extra seats
                </div>
                <h3 className="font-display font-extrabold text-2xl tracking-tight mt-1">
                  +{extra} extra seat{extra === 1 ? "" : "s"}
                </h3>
                <p className="text-xs text-ink-500 mt-1">
                  Adds <span className="font-mono">${cost}</span> to your monthly bill.
                  Tied to this account.
                </p>
              </div>

              <div className="inline-flex items-center gap-2 bg-ink-50 border border-ink-100 rounded-sm p-1">
                <button
                  data-testid="team-seats-decrement"
                  onClick={() => updateSeats(extra - 1)}
                  disabled={busy || extra <= 0}
                  className="grid place-items-center w-9 h-9 rounded-sm text-ink-700 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span
                  data-testid="team-seats-value"
                  className="font-mono font-bold text-lg text-ink-900 min-w-[40px] text-center"
                >
                  {extra}
                </span>
                <button
                  data-testid="team-seats-increment"
                  onClick={() => updateSeats(extra + 1)}
                  disabled={busy || extra >= team.max_extra_seats}
                  className="grid place-items-center w-9 h-9 rounded-sm text-ink-700 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Live monthly summary */}
            <div className="mt-6 pt-5 border-t border-ink-100 space-y-2 text-sm">
              <Row label={`${tier.charAt(0).toUpperCase() + tier.slice(1)} plan`} value={`$${user?.tier_model && team.included_seats !== null ? "" : ""}`}>
                <span data-testid="team-cost-base" className="font-mono text-ink-700">
                  base price
                </span>
              </Row>
              <Row label={`Extra seats × ${extra}`}>
                <span data-testid="team-cost-extras" className="font-mono text-ink-700">
                  + ${cost}/mo
                </span>
              </Row>
              <Row label="Add-on running total">
                <span
                  data-testid="team-cost-total"
                  className="font-mono font-bold text-ink-900"
                >
                  ${cost}/mo
                </span>
              </Row>
              <p className="text-[11px] text-ink-400 pt-2">
                Demo mode · changes save instantly · billed when Razorpay is wired.
              </p>
            </div>
          </div>

          {/* Members list + invite form */}
          <div
            data-testid="team-members"
            className="mt-6 bg-white border border-ink-100 rounded-sm overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                Team members · {(team.members?.length || 0) + invites.filter((i) => i.status === "pending").length} of {total}
              </div>
            </div>

            {/* Invite form */}
            {!isFree && (
              <form
                onSubmit={sendInvite}
                className="px-6 py-4 border-b border-ink-100 bg-ink-50/50 flex items-center gap-3"
                data-testid="invite-form"
              >
                <Mail className="w-4 h-4 text-ink-400 shrink-0" />
                <Input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="teammate@company.com"
                  className="flex-1 h-10 rounded-sm border-ink-200 focus:border-brand focus-visible:ring-brand font-mono text-sm"
                  data-testid="invite-email-input"
                />
                <Button
                  type="submit"
                  disabled={inviteSending || !inviteEmail}
                  className="h-10 px-4 rounded-sm bg-ink-900 hover:bg-ink-800 text-white font-medium tracking-wide"
                  data-testid="invite-send-button"
                >
                  {inviteSending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5 mr-1.5" />
                      Send invite
                    </>
                  )}
                </Button>
              </form>
            )}

            <div>
              {(team.members || []).map((m) => (
                <div
                  key={m.id}
                  data-testid={`team-member-${m.id}`}
                  className="px-6 py-4 flex items-center justify-between border-b border-ink-100 last:border-b-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="grid place-items-center w-9 h-9 rounded-sm bg-ink-100 text-ink-700 font-mono font-bold text-sm">
                      {(m.name || m.email).slice(0, 1).toUpperCase()}
                    </div>
                    <div className="leading-tight">
                      <div className="text-sm font-semibold text-ink-900">{m.name}</div>
                      <div className="text-[11px] font-mono text-ink-500">{m.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.is_owner && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm bg-[#FFF8E1] text-[#F57F17]">
                        <Crown className="w-2.5 h-2.5" />
                        Owner
                      </span>
                    )}
                    <span className="text-[9px] uppercase tracking-widestx font-mono text-ink-400">
                      {m.role}
                    </span>
                  </div>
                </div>
              ))}

              {/* Pending invites */}
              {invites
                .filter((i) => i.status === "pending")
                .map((inv) => (
                  <div
                    key={inv.id}
                    data-testid={`invite-row-${inv.id}`}
                    className="px-6 py-4 flex items-center justify-between border-b border-ink-100 last:border-b-0"
                  >
                    <div className="flex items-center gap-3">
                      <div className="grid place-items-center w-9 h-9 rounded-sm bg-ink-100 text-ink-400">
                        <Clock className="w-4 h-4" />
                      </div>
                      <div className="leading-tight">
                        <div className="text-sm font-semibold text-ink-700">{inv.email}</div>
                        <div className="text-[11px] font-mono text-ink-400">Pending acceptance</div>
                      </div>
                    </div>
                    <button
                      onClick={() => cancelInvite(inv.id)}
                      className="text-[10px] uppercase tracking-widestx font-semibold text-ink-400 hover:text-red-600 inline-flex items-center gap-1"
                      data-testid={`cancel-invite-${inv.id}`}
                    >
                      <X className="w-3 h-3" />
                      Cancel
                    </button>
                  </div>
                ))}

              {/* Empty seat placeholders */}
              {Array.from({
                length: Math.max(
                  0,
                  (total || 0) -
                    (team.members?.length || 0) -
                    invites.filter((i) => i.status === "pending").length
                ),
              }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="px-6 py-4 flex items-center justify-between border-b border-ink-100 last:border-b-0 bg-ink-50/40"
                >
                  <div className="flex items-center gap-3">
                    <div className="grid place-items-center w-9 h-9 rounded-sm bg-ink-100 text-ink-300">
                      <Users className="w-4 h-4" />
                    </div>
                    <div className="text-sm text-ink-400 italic">
                      Open seat — invite a teammate above
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, suffix, highlight = false, accent = false }) {
  return (
    <div
      className={`p-4 rounded-sm border ${
        highlight
          ? "bg-ink-900 text-white border-ink-900"
          : accent
          ? "bg-[#FFF8E1] border-[#F57F17]/30"
          : "bg-white border-ink-100"
      }`}
    >
      <div
        className={`text-[10px] uppercase tracking-widestx font-mono ${
          highlight ? "text-white/60" : "text-ink-400"
        }`}
      >
        {label}
      </div>
      <div
        className={`font-display font-black text-3xl tracking-tighter mt-1 leading-none ${
          highlight ? "text-white" : accent ? "text-[#F57F17]" : "text-ink-900"
        }`}
      >
        {value}
      </div>
      <div
        className={`text-[10px] mt-1 ${
          highlight ? "text-white/60" : "text-ink-400"
        }`}
      >
        {suffix}
      </div>
    </div>
  );
}

function Row({ label, value, children }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-ink-500">{label}</span>
      <span className="font-mono">{children || value}</span>
    </div>
  );
}
